import { ButtonLink } from '@toggle/design-system';
import { ThemeCompany } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { config } from '~/config/app.config';
import { WebOnlyFeat } from '~/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { DrawerSectionHeader } from '~/views/theme-overview/theme-overview-drawer/components/drawer-section-header/DrawerSectionHeader';
import { THEME_TAB_COMPANIES } from '~/views/theme-overview/theme-overview-header/ThemeOverviewHeader';

import { CompanyItem } from './company-item/CompanyItem';
import styles from './TopCompaniesSection.module.scss';

interface TopCompaniesSectionProps {
  companies: ThemeCompany[];
  remainingCount: number;
}

export const TopCompaniesSection = ({
  companies,
  remainingCount,
}: TopCompaniesSectionProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackThemeOpen } = usePosthogEvents();

  const openAssetOverview = (value: string) => {
    navigateToPage({ entityTag: value });
  };

  const openThemeOverview = () => {
    const themeId = params[queryKeys.themeId];
    const newSearchParams = new URLSearchParams();
    newSearchParams.set(queryKeys.activeSubTab, THEME_TAB_COMPANIES);
    trackThemeOpen(POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER);
    navigateToTheme({
      themeId: themeId || '',
      searchParams: newSearchParams,
    });
  };

  return (
    <ErrorBoundaryWrapper>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <DrawerSectionHeader
            className={styles.sectionHeader}
            title={t('themeOverview.topCompaniesTitle', {
              total: companies.length,
            })}
            description={t('themeOverview.rankedBySignificance')}
          />
          <ErrorBoundaryWrapper>
            <>
              <div className={styles.gridContainer}>
                {companies.map(company => (
                  <div
                    className={styles.companyItemWrapper}
                    key={company.company}
                  >
                    <CompanyItem
                      company={company}
                      onPillClick={
                        !config.app.isTeamsApp
                          ? () => openAssetOverview(company.company)
                          : () => void 0
                      }
                      iconSize={20}
                    />
                  </div>
                ))}
              </div>
              {!!remainingCount && (
                <WebOnlyFeat>
                  <ButtonLink
                    className={styles.styledButtonLink}
                    label={t('widget:themeOverview.viewMoreCompanies', {
                      total: remainingCount,
                    })}
                    iconName="ChevronLightRight"
                    iconPosition="right"
                    onClick={() => openThemeOverview()}
                  />
                </WebOnlyFeat>
              )}
            </>
          </ErrorBoundaryWrapper>
        </div>
      </div>
    </ErrorBoundaryWrapper>
  );
};
